if (!customElements.get('blog-page')) {
  class BlogPage extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('[data-hub-search-form]');
      this.input = this.querySelector('[data-hub-search-input]');
      this.resetButton = this.querySelector('[data-hub-search-reset]');
      this.status = this.querySelector('[data-hub-search-status]');
      this.curated = this.querySelector('[data-blog-curated]');
      this.searchView = this.querySelector('[data-blog-search-view]');
      this.searchResults = this.querySelector('[data-hub-search-results]');
      this.searchLoading = this.querySelector('[data-hub-search-loading]');
      this.searchError = this.querySelector('[data-hub-search-error]');
      this.searchFallback = this.querySelector('[data-hub-search-fallback]');
      this.sortSelect = this.querySelector('[data-blog-sort]');
      this.articleGrid = this.querySelector('[data-blog-article-grid]');
      this.blogPagination = this.querySelector('[data-blog-pagination]');
      this.totalBlogPages = Number.parseInt(this.blogPagination?.dataset.blogTotalPages, 10) || 1;
      this.blogPageSize = Number.parseInt(this.blogPagination?.dataset.blogPageSize, 10) || 12;
      this.currentBlogPage = Number.parseInt(this.blogPagination?.dataset.blogCurrentPage, 10) || 1;

      this.initSearch();
      this.initControls();
      this.initSort();
    }

    disconnectedCallback() {
      if (this.form && this.submitHandler) this.form.removeEventListener('submit', this.submitHandler);
      if (this.form && this.resetHandler) this.form.removeEventListener('reset', this.resetHandler);
      if (this.input && this.inputHandler) this.input.removeEventListener('input', this.inputHandler);
      if (this.searchView && this.paginationHandler) this.searchView.removeEventListener('click', this.paginationHandler);
      if (this.sortSelect && this.sortHandler) this.sortSelect.removeEventListener('change', this.sortHandler);
      if (this.blogPagination && this.sortPaginationHandler) this.blogPagination.removeEventListener('click', this.sortPaginationHandler);
      if (this.searchJump && this.searchJumpHandler) this.searchJump.removeEventListener('click', this.searchJumpHandler);
      if (this.backToTopLinks && this.backToTopHandler) {
        this.backToTopLinks.forEach((link) => link.removeEventListener('click', this.backToTopHandler));
      }
      if (this.popstateHandler) window.removeEventListener('popstate', this.popstateHandler);
      if (this.stickyStateHandler) {
        window.removeEventListener('scroll', this.stickyStateHandler);
        window.removeEventListener('resize', this.stickyStateHandler);
      }
      if (this.stickyStateFrame) window.cancelAnimationFrame(this.stickyStateFrame);

      window.clearTimeout(this.searchTimer);
      if (this.searchController) this.searchController.abort();
    }

    initSearch() {
      if (!this.form || !this.input || !this.curated || !this.searchView) return;

      this.submitHandler = this.handleSubmit.bind(this);
      this.inputHandler = this.handleInput.bind(this);
      this.resetHandler = this.handleReset.bind(this);
      this.paginationHandler = this.handlePagination.bind(this);
      this.popstateHandler = this.handlePopstate.bind(this);

      this.form.addEventListener('submit', this.submitHandler);
      this.form.addEventListener('reset', this.resetHandler);
      this.input.addEventListener('input', this.inputHandler);
      this.searchView.addEventListener('click', this.paginationHandler);
      window.addEventListener('popstate', this.popstateHandler);

      const params = new URL(window.location.href).searchParams;
      const query = params.get('q')?.trim() || '';
      const page = Number.parseInt(params.get('page'), 10) || 1;

      if (query) {
        this.input.value = query;
        this.resetButton.hidden = false;
        requestAnimationFrame(() => this.loadSearch(query, page));
      }
    }

    initControls() {
      this.searchJump = this.querySelector('[data-blog-search-jump]');
      this.backToTopLinks = Array.from(this.querySelectorAll('[data-blog-back-to-top]'));
      this.toolbar = this.querySelector('[data-blog-toolbar]');

      if (this.searchJump && this.input) {
        this.searchJumpHandler = (evt) => {
          evt.preventDefault();
          this.form.scrollIntoView({ behavior: this.scrollBehavior(), block: 'center' });
          window.setTimeout(() => this.input.focus({ preventScroll: true }), 300);
        };
        this.searchJump.addEventListener('click', this.searchJumpHandler);
      }

      if (this.backToTopLinks.length) {
        this.backToTopHandler = (evt) => {
          const target = document.querySelector(evt.currentTarget.hash);
          if (!target) return;

          evt.preventDefault();
          target.scrollIntoView({ behavior: this.scrollBehavior(), block: 'start' });
          window.history.replaceState({}, '', evt.currentTarget.hash);
        };
        this.backToTopLinks.forEach((link) => link.addEventListener('click', this.backToTopHandler));
      }

      if (this.toolbar) {
        this.stickyStateHandler = () => {
          if (this.stickyStateFrame) return;
          this.stickyStateFrame = window.requestAnimationFrame(() => {
            this.stickyStateFrame = null;
            this.updateStickyState();
          });
        };
        window.addEventListener('scroll', this.stickyStateHandler, { passive: true });
        window.addEventListener('resize', this.stickyStateHandler);
        requestAnimationFrame(() => this.updateStickyState());
      }
    }

    updateStickyState() {
      const stickyTop = Number.parseFloat(window.getComputedStyle(this.toolbar).top) || 0;
      const toolbarRect = this.toolbar.getBoundingClientRect();
      const sectionRect = this.getBoundingClientRect();
      const isStuck = Math.abs(toolbarRect.top - stickyTop) <= 2 && sectionRect.top < stickyTop;

      this.toolbar.classList.toggle('blog-page__toolbar--stuck', isStuck);
      this.classList.toggle('is-toolbar-stuck', isStuck);
    }

    initSort() {
      if (!this.sortSelect || !this.articleGrid) return;

      this.sortHandler = () => this.handleSortChange();
      this.sortPaginationHandler = this.handleSortPagination.bind(this);
      this.sortSelect.addEventListener('change', this.sortHandler);
      this.blogPagination?.addEventListener('click', this.sortPaginationHandler);
    }

    async handleSortChange() {
      if (!this.articleGrid) return;

      if (!this.allArticles && this.totalBlogPages > 1) {
        await this.loadAllArticles();
      }

      this.sortArticles(1);
    }

    async loadAllArticles() {
      this.sortSelect.disabled = true;
      this.blogPagination.dataset.pauseInfiniteScroll = 'true';
      this.querySelector('[data-blog-toolbar]')?.setAttribute('aria-busy', 'true');
      this.curated?.classList.add('is-loading');

      try {
        const pageRequests = [];
        for (let page = 1; page <= this.totalBlogPages; page += 1) {
          if (page === this.currentBlogPage) continue;

          const url = new URL(this.dataset.blogUrl, window.location.origin);
          url.searchParams.set('section_id', this.dataset.sectionId);
          url.searchParams.set('page', page);
          pageRequests.push(fetch(url).then((response) => {
            if (!response.ok) throw new Error(`Blog page request failed with ${response.status}`);
            return response.text();
          }));
        }

        const documents = await Promise.all(pageRequests);
        const articles = Array.from(this.articleGrid.querySelectorAll('[data-blog-article]'));

        documents.forEach((html) => {
          const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
          parsedDocument.querySelectorAll('[data-blog-article-grid] [data-blog-article]').forEach((article) => {
            articles.push(article);
          });
        });

        const uniqueArticles = new Map();
        articles.forEach((article) => uniqueArticles.set(article.dataset.articleUrl, article));
        this.allArticles = Array.from(uniqueArticles.values());
      } catch (error) {
        this.allArticles = Array.from(this.articleGrid.querySelectorAll('[data-blog-article]'));
        if (this.status) this.status.textContent = 'Sorting is currently limited to the articles on this page.';
      } finally {
        this.sortSelect.disabled = false;
        this.querySelector('[data-blog-toolbar]')?.removeAttribute('aria-busy');
        this.curated?.classList.remove('is-loading');
      }
    }

    sortArticles(page = 1) {
      if (!this.articleGrid) return;

      const mode = this.sortSelect.value;
      const articles = Array.from(this.allArticles || this.articleGrid.querySelectorAll('[data-blog-article]'));
      const compareTitle = (a, b) => a.dataset.articleTitle.localeCompare(b.dataset.articleTitle);
      const compareDate = (a, b) => Number(a.dataset.publishedAt) - Number(b.dataset.publishedAt);

      articles.sort((a, b) => {
        if (mode === 'oldest') return compareDate(a, b);
        if (mode === 'title-ascending') return compareTitle(a, b);
        if (mode === 'title-descending') return compareTitle(b, a);
        return compareDate(b, a);
      });

      this.sortedArticles = articles;
      this.renderSortedPage(page);
    }

    renderSortedPage(page) {
      const pageCount = Math.max(1, Math.ceil(this.sortedArticles.length / this.blogPageSize));
      const safePage = Math.min(Math.max(page, 1), pageCount);
      const start = (safePage - 1) * this.blogPageSize;
      const visibleArticles = this.sortedArticles.slice(start, start + this.blogPageSize);

      this.articleGrid.replaceChildren(...visibleArticles.map((article) => article.cloneNode(true)));
      this.renderSortPagination(safePage, pageCount);
      this.currentSortedPage = safePage;
    }

    renderSortPagination(currentPage, pageCount) {
      this.blogPagination.querySelectorAll('hr, nav').forEach((element) => element.remove());
      if (pageCount <= 1) return;

      const divider = document.createElement('hr');
      divider.className = 'mt-section';

      const pagination = document.createElement('nav');
      pagination.className = 'blog-page__sort-pagination';
      pagination.setAttribute('aria-label', 'Blog pagination');
      pagination.innerHTML = `
        <button type="button" class="blog-page__sort-page" data-blog-sort-page="${currentPage - 1}" ${currentPage === 1 ? 'disabled' : ''}>
          <span aria-hidden="true">←</span> Previous
        </button>
        <span>Page ${currentPage} of ${pageCount}</span>
        <button type="button" class="blog-page__sort-page" data-blog-sort-page="${currentPage + 1}" ${currentPage === pageCount ? 'disabled' : ''}>
          Next <span aria-hidden="true">→</span>
        </button>`;

      this.blogPagination.append(divider, pagination);
    }

    handleSortPagination(evt) {
      const button = evt.target.closest('[data-blog-sort-page]');
      if (!button || button.disabled || !this.sortedArticles) return;

      this.renderSortedPage(Number.parseInt(button.dataset.blogSortPage, 10) || 1);
      this.articleGrid.scrollIntoView({ behavior: this.scrollBehavior(), block: 'start' });
    }

    handleSubmit(evt) {
      evt.preventDefault();
      window.clearTimeout(this.searchTimer);

      const query = this.input.value.trim();
      if (!query) {
        this.restoreArticles({ updateHistory: true });
        return;
      }

      const currentQuery = new URL(window.location.href).searchParams.get('q') || '';
      this.loadSearch(query, 1, { historyMode: currentQuery === query ? 'replace' : 'push' });
    }

    handleInput() {
      const query = this.input.value.trim();
      this.resetButton.hidden = !query;
      window.clearTimeout(this.searchTimer);
      if (this.searchController) this.searchController.abort();

      if (!query) {
        this.restoreArticles({ updateHistory: true });
        return;
      }

      this.showSearchView();
      this.searchResults.replaceChildren();
      this.searchError.hidden = true;
      this.searchLoading.hidden = false;
      this.searchView.setAttribute('aria-busy', 'true');
      this.status.textContent = `Searching articles from all blogs for “${query}”.`;

      this.searchTimer = window.setTimeout(() => {
        const hasSearchHistory = new URL(window.location.href).searchParams.has('q');
        this.loadSearch(query, 1, { historyMode: hasSearchHistory ? 'replace' : 'push' });
      }, 350);
    }

    handleReset() {
      window.clearTimeout(this.searchTimer);
      window.setTimeout(() => this.restoreArticles({ updateHistory: true }), 0);
    }

    handlePagination(evt) {
      const link = evt.target.closest('.pagination a[href]');
      if (!link || !this.searchView.contains(link)) return;

      evt.preventDefault();
      const url = new URL(link.href, window.location.origin);
      const page = Number.parseInt(url.searchParams.get('page'), 10) || 1;
      const query = this.input.value.trim();
      if (query) this.loadSearch(query, page, { historyMode: 'push', scrollToResults: true });
    }

    handlePopstate() {
      const params = new URL(window.location.href).searchParams;
      const query = params.get('q')?.trim() || '';
      const page = Number.parseInt(params.get('page'), 10) || 1;

      this.input.value = query;
      this.resetButton.hidden = !query;
      if (query) this.loadSearch(query, page);
      else this.restoreArticles();
    }

    async loadSearch(query, page = 1, options = {}) {
      const { historyMode = null, scrollToResults = false } = options;
      if (!query) return;

      if (this.searchController) this.searchController.abort();
      this.searchController = new AbortController();
      const activeController = this.searchController;
      const requestUrl = this.buildSearchUrl(query, page, true);
      const fallbackUrl = this.buildSearchUrl(query, page, false);

      this.showSearchView();
      this.searchLoading.hidden = false;
      this.searchError.hidden = true;
      this.searchView.setAttribute('aria-busy', 'true');
      this.status.textContent = `Searching articles from all blogs for “${query}”.`;
      this.searchFallback.href = fallbackUrl;

      try {
        const response = await fetch(requestUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: activeController.signal
        });
        if (!response.ok) throw new Error(`Search request failed with ${response.status}`);

        const html = await response.text();
        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const remoteResults = parsedDocument.querySelector('[data-hub-remote-results]');
        if (!remoteResults) throw new Error('Search results markup was not returned');

        this.searchResults.innerHTML = remoteResults.outerHTML;
        const count = Number.parseInt(remoteResults.dataset.totalResults, 10) || 0;
        this.status.textContent = `${count} ${count === 1 ? 'article' : 'articles'} found across all blogs for “${query}”.`;
        if (historyMode) this.updateBrowserUrl(query, page, historyMode);
        if (scrollToResults) this.searchView.scrollIntoView({ behavior: this.scrollBehavior(), block: 'start' });
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.searchResults.replaceChildren();
        this.searchError.hidden = false;
        this.status.textContent = 'The article search could not be loaded. Please try again.';
      } finally {
        if (this.searchController === activeController) {
          this.searchLoading.hidden = true;
          this.searchView.removeAttribute('aria-busy');
        }
      }
    }

    buildSearchUrl(query, page, includeSection) {
      const url = new URL(this.form.action, window.location.origin);
      const params = new URLSearchParams(new FormData(this.form));
      params.set('q', query);
      if (page > 1) params.set('page', page);
      else params.delete('page');
      if (includeSection) params.set('section_id', this.form.dataset.searchSectionId);
      url.search = params.toString();
      return url.toString();
    }

    showSearchView() {
      this.classList.add('is-searching');
      this.curated.hidden = true;
      this.searchView.hidden = false;
      if (this.sortSelect) this.sortSelect.disabled = true;
    }

    restoreArticles(options = {}) {
      const { updateHistory = false } = options;
      if (this.searchController) this.searchController.abort();

      this.classList.remove('is-searching');
      this.curated.hidden = false;
      this.searchView.hidden = true;
      this.searchLoading.hidden = true;
      this.searchError.hidden = true;
      this.searchResults.replaceChildren();
      this.searchView.removeAttribute('aria-busy');
      this.status.textContent = '';
      this.resetButton.hidden = !this.input.value.trim();
      if (this.sortSelect) this.sortSelect.disabled = false;

      if (updateHistory) {
        const mode = new URL(window.location.href).searchParams.has('q') ? 'push' : 'replace';
        this.updateBrowserUrl('', 1, mode);
      }
    }

    updateBrowserUrl(query, page, mode) {
      const url = new URL(window.location.href);
      if (query) {
        url.searchParams.set('q', query);
        if (page > 1) url.searchParams.set('page', page);
        else url.searchParams.delete('page');
      } else {
        url.searchParams.delete('q');
        url.searchParams.delete('page');
      }
      window.history[`${mode}State`]({}, '', url);
    }

    scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }
  }

  customElements.define('blog-page', BlogPage);
}
